// src/components/Footer.js - 하단 푸터 컴포넌트
import React, { useState, useEffect, useRef } from 'react';
import { ChevronUp } from 'lucide-react';

const Footer = ({ setCurrentPage }) => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const footerRef = useRef(null);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;

      // 300px 이상 스크롤했을 때만 표시
      const shouldShow = scrollPosition > 300;
      setShowScrollButton(shouldShow);

      // Footer 영역 감지
      if (footerRef.current) {
        const footerRect = footerRef.current.getBoundingClientRect();
        const footerInView = footerRect.top < windowHeight;
        setIsFooterVisible(footerInView);
      }
    };

    // 초기 실행
    handleScroll();

    // 스크롤 이벤트 리스너 등록
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <footer ref={footerRef} className="bg-gray-800 border-t border-gray-700 mt-12 relative">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* 회사 정보 */}
          <div>
            <h3 className="text-lg font-bold text-gray-100 mb-4">Bluebook Go</h3>
            <p className="text-sm text-gray-300 mb-2">
              수능·내신 영어 대비<br />
              프리미엄 영어 학습 플랫폼
            </p>
            <p className="text-xs text-gray-400 mt-3">
              🌐 https://bluebookgo.com
            </p>
          </div>

          {/* 빠른 링크 */}
          <div>
            <h3 className="text-lg font-bold text-gray-100 mb-4">빠른 링크</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <button
                  onClick={() => setCurrentPage('library')}
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  📚 영어자료실
                </button>
              </li>
              <li>
                <button
                  onClick={() => setCurrentPage('pricing')}
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  💎 구독 안내
                </button>
              </li>
              <li>
                <button
                  onClick={() => setCurrentPage('notice')}
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  📢 공지사항
                </button>
              </li>
              <li>
                <button
                  onClick={() => setCurrentPage('faq')}
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  ❓ FAQ
                </button>
              </li>
            </ul>
          </div>

          {/* 고객 지원 및 법적 정보 */}
          <div>
            <h3 className="text-lg font-bold text-gray-100 mb-4">고객 지원</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href="mailto:support@bluebookgo.com"
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  📧 support@bluebookgo.com
                </a>
              </li>
              <li className="pt-2 border-t border-gray-600 mt-2">
                <button
                  onClick={() => setCurrentPage('terms')}
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  이용약관
                </button>
              </li>
              <li>
                <button
                  onClick={() => setCurrentPage('privacy')}
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  개인정보처리방침
                </button>
              </li>
              <li>
                <button
                  onClick={() => setCurrentPage('refund')}
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  환불 규정
                </button>
              </li>
              <li>
                <button
                  onClick={() => setCurrentPage('ecommerce')}
                  className="text-gray-300 hover:text-blue-400 transition-colors"
                >
                  전자상거래 약관
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* 사업자 정보 */}
        <div className="mt-8 pt-6 border-t border-gray-600 text-center">
          <p className="text-xs text-gray-400">
            상호: 디엔드엔 | 대표자: 윤종주 | 사업자등록번호: 686-17-02019
          </p>
          <p className="text-xs text-gray-400">
            통신판매업신고: 제2025-부산진-1419호 | 주소: 부산광역시 부산진구 서전로 11-1, 지하1층(부전동)
          </p>
          <p className="text-xs text-gray-400">
            대표번호: 010-2002-5818
          </p>
          <p className="text-xs text-gray-300 mb-2">
            © 2025 Bluebook Go. All rights reserved.
          </p>
          <p className="text-xs text-gray-400 mb-3">
            본 사이트의 모든 콘텐츠는 저작권법의 보호를 받습니다.
          </p>
        </div>
      </div>

      {/* 맨 위로 올라가기 버튼 - 스크롤 시에만 표시, Footer 영역에서는 위로 이동 */}
      <button
        onClick={scrollToTop}
        className={`fixed right-4 md:right-8 bg-blue-600/70 hover:bg-blue-600 backdrop-blur-sm text-white rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110 z-40
          ${showScrollButton ? 'opacity-100' : 'opacity-0 pointer-events-none'}
          ${isFooterVisible ? 'bottom-[280px] md:bottom-[320px]' : 'bottom-20 md:bottom-8'}`}
        aria-label="맨 위로 올라가기"
        aria-hidden={!showScrollButton}
      >
        <ChevronUp className="w-6 h-6" />
      </button>
    </footer>
  );
};

export default Footer;
